"use client";

import * as React from "react";
import { Button } from "@baluu/ui/components/button";
import { Input } from "@baluu/ui/components/input";
import { Spinner } from "@baluu/ui/components/spinner";
import { Tag, X } from "lucide-react";
import {
  MAX_DISCOUNT_CODES,
  type AppliedDiscount,
} from "@baluu/ui/lib/checkout/discount-codes";

/**
 * The several-codes field a basket checkout shows when the school allows
 * discount stacking (and for a booking request): applied codes as removable
 * chips, and a field to add another. `onApply` throws the reason a code is
 * refused. Shared by the widget's basket checkout and the webcomponents
 * overlay; no portals, so it works inside a shadow root.
 */
export function DiscountCodesSection({
  appliedDiscounts,
  onApply,
  onRemove,
}: {
  appliedDiscounts: AppliedDiscount[];
  onApply: (code: string) => Promise<void>;
  onRemove: (code: string) => void;
}) {
  const [inputVal, setInputVal] = React.useState("");
  const [applying, setApplying] = React.useState(false);
  const [applyError, setApplyError] = React.useState<string | null>(null);

  const apply = async () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    setApplying(true);
    setApplyError(null);
    try {
      await onApply(trimmed);
      setInputVal("");
    } catch (err) {
      setApplyError((err instanceof Error && err.message) || "Invalid code");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-2 py-1">
      <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
        <Tag className="w-3.5 h-3.5" />
        Discount Codes
      </p>
      {appliedDiscounts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {appliedDiscounts.map(({ code }) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-0.5"
            >
              {code}
              <button
                type="button"
                onClick={() => onRemove(code)}
                className="ml-0.5 hover:text-green-900"
                aria-label={`Remove ${code}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {appliedDiscounts.length < MAX_DISCOUNT_CODES && (
        <div className="flex gap-2">
          <Input
            value={inputVal}
            onChange={(e) => { setInputVal(e.target.value); setApplyError(null); }}
            placeholder={appliedDiscounts.length ? "Add another code" : "Discount code"}
            className="h-8 text-sm"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void apply(); } }}
            disabled={applying}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void apply()}
            disabled={applying || !inputVal.trim()}
            className="h-8 whitespace-nowrap"
          >
            {applying ? <Spinner className="w-3 h-3" /> : "Apply"}
          </Button>
        </div>
      )}
      {applyError && <p className="text-xs text-destructive">{applyError}</p>}
    </div>
  );
}
